import { Fragment, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import { UrlSection } from "./components/UrlSection";
import { BrandSection } from "./components/BrandSection";
import { ColorsSection } from "./components/ColorsSection";
import { TypographySection } from "./components/TypographySection";
import { VoiceSection } from "./components/VoiceSection";
import { OfferSection } from "./components/OfferSection";
import { CompetitorsSection } from "./components/CompetitorsSection";
import { SocialSection } from "./components/SocialSection";
import { BuyerPersonaSection } from "./components/BuyerPersonaSection";
import { CountrySection } from "./components/CountrySection";
import { LogoSection } from "./components/LogoSection";
import { AdditionalAssetsSection } from "./components/AdditionalAssetsSection";
import { ProductImagesSection } from "./components/ProductImagesSection";
import { ICPSection } from "./components/ICPSection";
import SelectBrandSetupVersion from "./components/SelectBrandSetupVersion";
import BrandSetupGroupHeader from "./components/BrandSetupGroupHeader";
import { useTranslationApp } from "../../../lib/i18next";
import Button from "../../../components/Buttons/Button";
import { INPUT_COLOR_BORDER } from "../../../utils/colors";
import Loader from "../../../components/Loaders/Loader";
import {
  BUTTON_SAVE_CHANGES,
  BRAND_SETUP_LOADING_GETTING_WEBSITE_INFORMATION,
  BRAND_SETUP_LOADING_ANALYZING_BRAND,
  BRAND_SETUP_LOADING_GENERATING_BRAND_PROFILE,
  BRAND_SETUP_LOADING_FINALIZING_DETAILS,
  BRAND_SETUP_GROUP_VISUAL_IDENTITY_TITLE,
  BRAND_SETUP_GROUP_VISUAL_IDENTITY_DESCRIPTION,
  BRAND_SETUP_GROUP_AUDIENCE_TITLE,
  BRAND_SETUP_GROUP_AUDIENCE_DESCRIPTION,
  BRAND_SETUP_GROUP_COPY_TITLE,
  BRAND_SETUP_GROUP_COPY_DESCRIPTION,
} from "../../../i18n/keysTranslations";
import { useBrandSetup } from "./hooks/useBrandSetup";
import ModalSuccessBrandSetup from "../../../components/Modal/ModalSuccessBrandSetup";
import ModalCreateAccountFromAnonymous from "../../../components/Modal/ModalCreateAccountFromAnonymous";
import LinearLoaderWithTime from "../../../components/Loaders/LinearLoaderWithTime";
import {
  CHECKLIST_BRAND_SETUP,
  IS_ADLE_DOMAIN,
  TIME_LOADER_GENERATING_CAMPAIGN_ADVANCED,
} from "../../../utils/constants";
import { AdleLogoLoader } from "../../../assets/svg/AdleLogoLoader";
import { checkAndSaveUserOnboardingChecklist } from "../../../actions/adsCampaigns";
import { useDispatchApp } from "../../../lib/redux";
import { getRoleCapability } from "../../../actions/getters";
import { BrandSetupReadOnlyContext } from "./context/brandSetupReadOnlyContext";
import { COMPETITOR_SCOPE_COMPETITORS_SECTION_ID } from "../../../utils/competitorScope/competitorScopeValidation";

// Section error mapping: maps section IDs to their field names
const SECTION_ERROR_MAP = {
  "brand-section": ["businessName", "businessDescription"],
  "colors-section": ["businessLookAndFeel", "businessColors"],
  "typography-section": ["businessTypography"],
  "voice-section": ["businessTextStyle"],
  "offer-section": ["businessOffering", "businessValueProposition"],
  "target-audience-section": ["businessIdealCustomerProfile"],
  "competitors-section": ["businessCompetitors"],
  "social-section": ["socialNetworks"],
  "buyer-persona-section": ["businessBuyerPerson"],
  "country-section": ["businessCountry"],
  "logo-section": ["logoUrl"],
  "additional-assets-section": ["additionalAssets"],
  "products-section": ["businessProductImages"],
};

const BRAND_SETUP_EXTRACTION_LOADER_ID = "brand-setup-extraction-loader";

const BRAND_SETUP_GROUPS = [
  {
    key: "visual",
    titleKey: BRAND_SETUP_GROUP_VISUAL_IDENTITY_TITLE,
    descriptionKey: BRAND_SETUP_GROUP_VISUAL_IDENTITY_DESCRIPTION,
    sections: [
      { id: "colors-section", Component: ColorsSection },
      { id: "typography-section", Component: TypographySection },
      { id: "logo-section", Component: LogoSection },
      { id: "additional-assets-section", Component: AdditionalAssetsSection },
      { id: "products-section", Component: ProductImagesSection },
    ],
  },
  {
    key: "copy",
    titleKey: BRAND_SETUP_GROUP_COPY_TITLE,
    descriptionKey: BRAND_SETUP_GROUP_COPY_DESCRIPTION,
    sections: [
      { id: "voice-section", Component: VoiceSection },
      { id: "offer-section", Component: OfferSection },
    ],
  },
  {
    key: "audience",
    titleKey: BRAND_SETUP_GROUP_AUDIENCE_TITLE,
    descriptionKey: BRAND_SETUP_GROUP_AUDIENCE_DESCRIPTION,
    sections: [
      { id: "target-audience-section", Component: ICPSection },
      { id: "buyer-persona-section", Component: BuyerPersonaSection },
      { id: "country-section", Component: CountrySection },
      { id: "competitors-section", Component: CompetitorsSection },
      { id: "social-section", Component: SocialSection },
    ],
  },
];

export const BrandSetup = () => {
  const dispatch = useDispatchApp();
  const location = useLocation();
  const isBrandSetupReadOnly = !dispatch(
    getRoleCapability("canEditBrandSetup")
  );
  const formMethods = useBrandSetup();
  const [modalSuccessBrandSetupOpen, setModalSuccessBrandSetupOpen] =
    useState(false);
  const [
    modalCreateAccountFromAnonymousOpen,
    setModalCreateAccountFromAnonymousOpen,
  ] = useState(false);

  const isExtracted = formMethods.isExtracted;
  const isExtracting = formMethods.isExtracting;

  useEffect(() => {
    if (!isExtracting) return;

    const timeoutId = setTimeout(() => {
      const element = document.getElementById(BRAND_SETUP_EXTRACTION_LOADER_ID);
      if (element) {
        element.scrollIntoView({ behavior: "smooth", block: "end" });
      }
    }, 200);

    return () => clearTimeout(timeoutId);
  }, [isExtracting]);

  useEffect(() => {
    if (formMethods.isLoading || !formMethods.control) return;
    if (!isExtracted || isExtracting) return;
    if (location.hash !== `#${COMPETITOR_SCOPE_COMPETITORS_SECTION_ID}`) return;

    const element = document.getElementById(
      COMPETITOR_SCOPE_COMPETITORS_SECTION_ID
    );
    if (element) {
      element.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [
    formMethods.control,
    formMethods.isLoading,
    isExtracted,
    isExtracting,
    location.hash,
  ]);

  if (formMethods.isLoading || !formMethods.control) {
    return <Loader />;
  }

  // Helper function to check if a section has errors
  const getSectionErrors = (sectionId) => {
    const fieldNames = SECTION_ERROR_MAP[sectionId] || [];
    return fieldNames.some((fieldName) => formMethods.errors?.[fieldName]);
  };

  // Helper function to scroll to first error section
  const scrollToFirstError = () => {
    const sectionIds = Object.keys(SECTION_ERROR_MAP);
    for (const sectionId of sectionIds) {
      if (getSectionErrors(sectionId)) {
        const element = document.getElementById(sectionId);
        if (element) {
          element.scrollIntoView({ behavior: "smooth", block: "center" });
          break;
        }
      }
    }
  };

  const onHandleSave = async () => {
    // Trigger validation on all fields
    const isValid = await formMethods.trigger();
    if (!isValid) {
      // Scroll to first error if validation fails
      setTimeout(() => scrollToFirstError(), 100);
      return;
    }

    // If valid, proceed with form submission
    formMethods.handleSubmit(async (data) => {
      const result = await formMethods.saveFormData(data);
      if (result?.success) {
        dispatch(checkAndSaveUserOnboardingChecklist(CHECKLIST_BRAND_SETUP));
      }
      if (result?.success && result?.showSuccessModal) {
        setModalSuccessBrandSetupOpen(true);
      }
      if (result?.success && result?.showCreateAccountModal) {
        setModalCreateAccountFromAnonymousOpen(true);
      }
    })();
  };

  const onCloseCreateAccountModal = () => {
    setModalCreateAccountFromAnonymousOpen(false);
    setModalSuccessBrandSetupOpen(true);
  };

  return (
    <BrandSetupReadOnlyContext.Provider value={isBrandSetupReadOnly}>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          pb: { xs: isBrandSetupReadOnly ? 0 : "80px", md: 0 },
        }}
      >
        <Box
          component="fieldset"
          disabled={isBrandSetupReadOnly}
          sx={{
            display: "flex",
            flexDirection: "column",
            border: "none",
            margin: 0,
            padding: 0,
            minWidth: 0,
          }}
        >
          <Grid container spacing={2}>
            {!isExtracting &&
              isExtracted &&
              formMethods.versions?.length > 1 && (
                <Grid item xs={12}>
                  <SelectBrandSetupVersion
                    versions={formMethods.versions}
                    selectedVersionID={formMethods.selectedVersionID}
                    onSelectVersion={formMethods.onSelectVersion}
                    isLoading={formMethods.isLoadingVersions}
                    disabled={isBrandSetupReadOnly}
                  />
                </Grid>
              )}
            <Grid item xs={12}>
              <UrlSection
                formMethods={formMethods}
                onReExtract={formMethods.handleReExtract}
                isReExtracting={formMethods.isReExtracting}
                isExtracting={formMethods.isExtracting}
                isExtracted={isExtracted}
                onNotHaveWebsite={formMethods.handleNotHaveWebsite}
                generationSources={formMethods.generationSources}
              />
            </Grid>
            {isExtracting && <BrandSetupExtractionLoader />}

            {!isExtracting && isExtracted && (
              <>
                <Grid item xs={12} id="brand-section">
                  <BrandSection
                    formMethods={formMethods}
                    onReExtract={formMethods.handleReExtract}
                    isReExtracting={formMethods.isReExtracting}
                    hasError={getSectionErrors("brand-section")}
                  />
                </Grid>
                {BRAND_SETUP_GROUPS.map(
                  ({ key, titleKey, descriptionKey, sections }) => (
                    <Fragment key={key}>
                      <Grid item xs={12}>
                        <BrandSetupGroupHeader
                          titleKey={titleKey}
                          descriptionKey={descriptionKey}
                        />
                      </Grid>
                      {sections
                        .filter(
                          ({ id }) =>
                            id !== "additional-assets-section" ||
                            formMethods.hasAdditionalAssets === true
                        )
                        .map(({ id, Component }) => (
                          <Grid
                            key={id}
                            item
                            xs={12}
                            lg={6}
                            sx={{ display: "flex" }}
                            id={id}
                          >
                            <Component
                              formMethods={formMethods}
                              hasError={getSectionErrors(id)}
                            />
                          </Grid>
                        ))}
                    </Fragment>
                  )
                )}
              </>
            )}
          </Grid>
        </Box>
        {!isBrandSetupReadOnly && (
          <BottomBar isSaving={formMethods.isSaving} onSubmit={onHandleSave} />
        )}

        {modalSuccessBrandSetupOpen && (
          <ModalSuccessBrandSetup
            onCloseModal={() => setModalSuccessBrandSetupOpen(false)}
          />
        )}
        {modalCreateAccountFromAnonymousOpen && (
          <ModalCreateAccountFromAnonymous
            modalOpen={modalCreateAccountFromAnonymousOpen}
            onCloseModal={onCloseCreateAccountModal}
          />
        )}
      </Box>
    </BrandSetupReadOnlyContext.Provider>
  );
};

const BrandSetupExtractionLoader = () => {
  const { t } = useTranslationApp();

  return (
    <Grid item xs={12}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          mt: 12,
        }}
      >
        <Box
          sx={{
            width: "100%",
            maxWidth: 700,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {IS_ADLE_DOMAIN && <AdleLogoLoader />}
          <LinearLoaderWithTime
            labelSide="bottom"
            time={TIME_LOADER_GENERATING_CAMPAIGN_ADVANCED}
            labels={[
              t(BRAND_SETUP_LOADING_GETTING_WEBSITE_INFORMATION),
              t(BRAND_SETUP_LOADING_ANALYZING_BRAND),
              t(BRAND_SETUP_LOADING_GENERATING_BRAND_PROFILE),
              t(BRAND_SETUP_LOADING_FINALIZING_DETAILS),
            ]}
          />
        </Box>
      </Box>
      <Box id={BRAND_SETUP_EXTRACTION_LOADER_ID} sx={{ height: 200 }} />
    </Grid>
  );
};

const BottomBar = ({ isSaving, onSubmit }) => {
  const { t } = useTranslationApp();

  return (
    <Box
      sx={{
        width: "100%",
        p: 2,
        borderTop: `1px solid ${INPUT_COLOR_BORDER}`,
        display: "flex",
        justifyContent: "flex-end",
        position: "fixed",
        bottom: 0,
        left: 0,
        right: 0,
        backgroundColor: "background.paper",
        zIndex: 1000,
      }}
    >
      <Button
        color="primary"
        type="button"
        loading={isSaving}
        onClick={onSubmit}
      >
        {t(BUTTON_SAVE_CHANGES)}
      </Button>
    </Box>
  );
};
